/** @format */
// @ts-check
import {TextField, TextFieldProps} from '@mui/material';
import {useEffect, useRef, useState} from 'react';

//Style import
import '../css/common/common.css';

// prettier-ignore
type propsType = {
  /**入力値応答 */
  setValue          : React.Dispatch<React.SetStateAction<any>> //入力が必須でない(undefined)場合があるため、anyにしている
  /**エラーチェック応答 */
  setError          : React.Dispatch<React.SetStateAction<boolean>>
  /**(任意)入力値チェック用正規表現（正規表現に当てはまる場合正常になる） */
  chkInputTxtOK     ?: RegExp | undefined
  /**(任意)入力値チェック用正規表現（正規表現に当てはまる場合エラーになる） */
  chkInputTxtError  ?: RegExp | undefined
  /**(任意)入力値チェック用正規表現（指定文字を含んでいる場合エラーになる） */
  chkInputProhibitedCharacter?:RegExp|undefined
  /**(任意)文字数チェック */
  chkLength         ?: number | undefined
  /**(任意)テキストボックス種類 (default:text) */
  type              ?: TextFieldProps['type']
  /**(任意)テキストボックスラベル */
  label             ?: TextFieldProps['label']
  /**(任意)初期value値 */
  defaultValue      ?: TextFieldProps['defaultValue']
  /**(任意)value値 */
  value             ?: TextFieldProps['value']
  /**(任意)ID名 */
  id                ?: TextFieldProps['id']
  /**(任意)クラス名 */
  class             ?: TextFieldProps['className']
  /**(任意)ヘルパーテキスト */
  helperText        ?: TextFieldProps['helperText']
  /**(任意)エラー発生時のヘルパーテキスト */
  errHelperText     ?: TextFieldProps['helperText']
  /**(任意)TextField種類 (default:standard) */
  variant           ?: TextFieldProps['variant']
  /**(任意)name属性 */
  name              ?: TextFieldProps['name']
  /**(任意)オートフォーカス */
  autoFocus         ?: TextFieldProps['autoFocus']
  /**(任意)垂直方向の間隔 */
  margin            ?: TextFieldProps['margin']
  /**(任意)キーダウンイベント */
  onKeyDown         ?: TextFieldProps['onKeyDown']
  /**(任意)有効無効 */
  disabled          ?: TextFieldProps['disabled']
  /**(任意)サイズ */
  size              ?: TextFieldProps['size']
  /**(任意)入力時のヒント */
  placeholder       ?: TextFieldProps['placeholder']
  /**(任意)横幅最大 */
  fullWidth         ?: TextFieldProps['fullWidth']
  /**(任意)入力欄に適用するプロパティ */
  InputProps        ?: TextFieldProps['InputProps'] //頭文字を小文字にすると別の引数になるため注意
}

/**
 * 入力チェック機能付テキストボックス
 * @param setValue          {(arg: string) => void | undefined}   入力値応答
 * @param setError          {(arg: boolean) => void | undefined}  エラーチェック応答
 * @param chkInputTxtOK     {RegExp | undefined}            (任意)入力値チェック用正規表現（正規表現に当てはまる場合正常になる）
 * @param chkInputTxtError  {RegExp | undefined}            (任意)入力値チェック用正規表現（正規表現に当てはまる場合エラーになる）
 * @param chkInputProhibitedCharacter  {RegExp | undefined} (任意)入力値チェック用正規表現（指定された文字を含んでいる場合エラーになる）
 * @param chkLength         {number | undefined}            (任意)文字数チェック
 * @param type              {TextFieldProps['type']}        (任意)テキストボックス種類 (default:text)
 * @param label             {TextFieldProps['label']}       (任意)テキストボックスラベル
 * @param defaultValue      {TextFieldProps['defaultValue']}(任意)初期value値
 * @param value             {TextFieldProps['value']}       (任意)value値
 * @param id                {TextFieldProps['id']}          (任意)ID
 * @param class             {TextFieldProps['className']}   (任意)クラス名
 * @param helperText        {TextFieldProps['helperText']}  (任意)ヘルパーテキスト
 * @param errHelperText     {TextFieldProps['helperText']}  (任意)エラー発生時のヘルパーテキスト
 * @param variant           {TextFieldProps['variant']}     (任意)TextField種類 (default:standard)
 * @param name              {TextFieldProps['name']}        (任意)name属性
 * @param autoFocus         {TextFieldProps['autoFocus']}   (任意)オートフォーカス
 * @param margin            {TextFieldProps['margin']}      (任意)垂直方向の間隔
 * @param onKeyDown         {TextFieldProps['onKeyDown']}   (任意)キーダウンイベント
 * @param disabled          {TextFieldProps['disabled']}    (任意)有効無効
 * @param size              {TextFieldProps['size']}        (任意)サイズ
 * @param placeholder       {TextFieldProps['placeholder']} (任意)入力時のヒント
 * @param fullWidth         {TextFieldProps['fullWidth']}   (任意)横幅最大
 * @returns jsx.element
 */
export const ChkInputTextbox = (props: propsType) => {
  /** 初回レンダリングフラグ */
  const isFirstRender = useRef(true);
  /**テキストボックス入力エラー判定 */
  const [errStatus, setErrStatus] = useState(false);

  /**テキスト入力時エラー判定 */
  const chkErrInput = (e: {target: {value: string}}) => {
    /** テキストボックス入力値 */
    const val = e.target.value;
    //テキストボックス入力値エラー判定
    chkErrValue(val);
  };

  /** value値エラー判定処理 */
  const chkErrValue = (value: string) => {
    /** エラー有:true, エラー無:false */
    let errFlg: boolean = false;

    //最大文字数チェック
    if (props.chkLength !== undefined) {
      if (value.length > props.chkLength) {
        errFlg = true;
      }
    }

    //入力テキストチェック
    if (props.chkInputTxtOK !== undefined) {
      const regex = new RegExp(props.chkInputTxtOK);
      if (regex.test(value) === false) {
        errFlg = true;
      }
    }
    if (props.chkInputTxtError !== undefined) {
      const regex = new RegExp(props.chkInputTxtError);
      if (regex.test(value)) {
        errFlg = true;
      }
    }
    if (props.chkInputProhibitedCharacter !== undefined) {
      const regex = new RegExp(props.chkInputProhibitedCharacter);
      if (regex.test(value)) {
        errFlg = true;
      }
    }

    //エラー判定結果セット
    setErrStatus(errFlg);

    //入力異常判定結果
    props.setError(errFlg);
    //テキストボックス入力値
    props.setValue(value);
  };

  //テキスト入力以外でvalueの値が変化、
  //または入力欄の有効無効が変化した際のvalue値エラー判定処理
  useEffect(() => {
    // 初回レンダリング時はチェックは行わない
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof props.value !== 'string') return;
    //value値入力判定
    chkErrValue(props.value);
  }, [props.value, props.disabled]);

  return (
    <>
      {/* prettier-ignore */}
      <TextField
        error       = {errStatus}
        id          = {props.id}
        label       = {props.label}
        value       = {props.value}
        className   = {props.class}
        name        = {props.name}
        helperText  = {errStatus ? props.errHelperText : props.helperText}
        onChange    = {chkErrInput}
        type        = {(props.type    === undefined) ? "text"     : props.type}
        variant     = {(props.variant === undefined) ? "standard" : props.variant}
        autoFocus   = {props.autoFocus === true}
        margin      = {props.margin}
        onKeyDown   = {props.onKeyDown}
        disabled    = {props.disabled}
        size        = {props.size}
        placeholder = {props.placeholder}
        fullWidth   = {props.fullWidth}
        InputProps  = {props.InputProps}
      />
    </>
  );
};
